// models/User.js
// Frontend model — wraps API response data into a class with helper methods
// Hierarchy: super_admin → admin → super_distributor → distributor → subdistributor (optional) → retailer → employee

export class User {
  constructor(data = {}) {
    this.id              = data._id || data.id || '';
    this._id             = data._id || data.id || '';
    this.customId        = data.customId        || '';
    this.firstName       = data.firstName       || '';
    this.lastName        = data.lastName        || '';
    this.email           = data.email           || '';
    this.phone           = data.phone           || '';
    this.role            = data.role            || 'retailer';
    this.companyId       = data.companyId       || null;   // populated object or ObjectId string
    this.parentId        = data.parentId        || null;   // populated object or ObjectId string
    this.ancestorIds     = data.ancestorIds     || [];
    this.avatar          = data.avatar          || null;
    this.avatarPublicId  = data.avatarPublicId  || null;
    this.isActive        = data.isActive  !== undefined ? data.isActive  : true;
    this.isVerified      = data.isVerified !== undefined ? data.isVerified : false;
    this.isBlocked       = data.isBlocked  !== undefined ? data.isBlocked  : false;
    this.blockReason     = data.blockReason     || '';
    this.permissions     = data.permissions     || [];
    this.commissionRate  = data.commissionRate  ?? 0;
    this.walletBalance   = data.wallet?.balance ?? data.walletBalance ?? 0;
    this.walletCurrency  = data.wallet?.currency ?? 'INR';
    this.kycVerified     = data.wallet?.kycVerified ?? false;
    this.address         = data.address         || {};
    this.preferences     = data.preferences     || {};
    this.employeeDetails = data.employeeDetails || {};
    this.lastLogin       = data.lastLogin       || null;
    this.lastActive      = data.lastActive      || null;
    this.joinDate        = data.createdAt       || data.joinDate || new Date().toISOString();
    this.createdAt       = data.createdAt       || new Date().toISOString();
    this.updatedAt       = data.updatedAt       || new Date().toISOString();
  }

  get fullName()    { return `${this.firstName} ${this.lastName}`.trim(); }
  get initials()    { return `${this.firstName[0] || ''}${this.lastName[0] || ''}`.toUpperCase(); }
  get displayRole() { return ROLE_LABELS[this.role] || this.role; }

  // Company name from populated field or fallback
  get companyName() {
    if (this.companyId && typeof this.companyId === 'object') return this.companyId.companyName || '';
    return '';
  }

  // Parent name from populated field or fallback
  get parentName() {
    if (this.parentId && typeof this.parentId === 'object')
      return `${this.parentId.firstName || ''} ${this.parentId.lastName || ''}`.trim();
    return '';
  }

  get parentRole() {
    if (this.parentId && typeof this.parentId === 'object') return this.parentId.role || '';
    return '';
  }

  getRoleColor()   { return ROLE_COLORS[this.role]   || '#64748b'; }

  getStatusColor() {
    if (this.isBlocked)  return '#ef4444';
    if (!this.isActive)  return '#94a3b8';
    if (!this.isVerified)return '#f59e0b';
    return '#10b981';
  }

  getStatusLabel() {
    if (this.isBlocked)  return 'Blocked';
    if (!this.isActive)  return 'Inactive';
    if (!this.isVerified)return 'Unverified';
    return 'Active';
  }

  getDaysSinceJoining() {
    return Math.floor((Date.now() - new Date(this.joinDate).getTime()) / 86400000);
  }

  getLastActiveLabel() {
    if (!this.lastActive) return 'Never';
    const days = Math.floor((Date.now() - new Date(this.lastActive).getTime()) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days}d ago`;
  }
}

// ── Constants ─────────────────────────────────────────────────────────────────
export const ROLE_HIERARCHY = [
  'super_admin', 'admin', 'super_distributor',
  'distributor', 'subdistributor', 'retailer', 'employee',
];

export const ROLE_LABELS = {
  super_admin:       'Super Admin',
  admin:             'Admin',
  super_distributor: 'Super Distributor',
  distributor:       'Distributor',
  subdistributor:    'Sub Distributor',
  retailer:          'Retailer',
  employee:          'Employee',
};

export const ROLE_COLORS = {
  super_admin:       '#7c3aed',
  admin:             '#2563eb',
  super_distributor: '#0891b2',
  distributor:       '#059669',
  subdistributor:    '#d97706',
  retailer:          '#dc2626',
  employee:          '#64748b',
};

// Who each role can create under themselves
export const ROLE_CAN_CREATE = {
  super_admin:       ['admin','super_distributor','distributor','subdistributor','retailer','employee'],
  admin:             ['super_distributor','distributor','subdistributor','retailer','employee'],
  super_distributor: ['distributor','subdistributor','retailer','employee'],
  distributor:       ['subdistributor','retailer','employee'],
  subdistributor:    ['retailer','employee'],
  retailer:          ['employee'],
  employee:          [],
};